import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Download, Eye, EyeOff, Loader2, ArrowRight, CheckCircle2, Circle, X } from 'lucide-react';
import { Site } from '../types';
import { callSiteApi } from '../utils';
import { toast } from '@/hooks/use-toast';

interface FormsTabProps {
  site: Site;
  searchTerm: string;
  formFilter: string;
  refreshTrigger: boolean;
}

interface FormSubmission {
  id: number;
  form_id: number;
  plugin: string;
  form_name: string;
  name: string;
  email: string;
  submitted_at: string;
  status: string;
  is_read: number;
  data: Array<{ label: string; value: string; type: string }>;
}

export function FormsTab({ site, searchTerm, formFilter, refreshTrigger }: FormsTabProps) {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const data = await callSiteApi(site.url, site.publicKey, site.secretKey, 'submissions/forms');
      if (data) {
        setIsEnabled(data.enabled !== false);
        if (data.submissions) {
          setSubmissions(data.submissions);
        }
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load form submissions.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [site.id, refreshTrigger]);

  const handleToggleRead = async (id: number, currentStatus: number) => {
    setActionLoading(id);
    try {
      const data = await callSiteApi(
        site.url,
        site.publicKey,
        site.secretKey,
        `submissions/forms/${id}`,
        'PUT',
        JSON.stringify({ is_read: currentStatus === 1 ? 0 : 1 })
      );

      if (data?.success) {
        setSubmissions(prev => prev.map(s => s.id === id ? { ...s, is_read: currentStatus === 1 ? 0 : 1 } : s));
        toast({
          title: currentStatus === 1 ? 'Marked as Unread' : 'Marked as Read',
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredSubmissions = submissions.filter(s => {
    const matchesForm = formFilter === 'all' || s.form_id.toString() === formFilter;
    const searchLow = searchTerm.toLowerCase();
    const matchesSearch = 
      s.name.toLowerCase().includes(searchLow) ||
      s.email.toLowerCase().includes(searchLow) ||
      s.form_name.toLowerCase().includes(searchLow);
    return matchesForm && matchesSearch;
  });

  // Show disabled state when form submissions feature is turned off
  if (!isLoading && isEnabled === false) {
    return (
      <div className="bg-card border border-border rounded-2xl shadow-sm">
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <div className="w-16 h-16 rounded-3xl bg-muted/30 flex items-center justify-center mb-4">
            <Database className="w-8 h-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-base font-bold text-foreground mb-1">Form Submissions Disabled</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Form submissions fetching is currently disabled for this site. Enable it in <span className="font-semibold text-foreground">Site Settings</span> to start collecting and viewing submissions from your form plugins.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground/70">
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">Fluent Forms</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">Gravity Forms</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">WPForms</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">Contact Form 7</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">Bit Forms</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">WS Forms</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">Bricks Forms</span>
            <span className="px-2 py-1 rounded-md bg-muted/40 border border-border/50">Bricksforge Pro</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="w-10 px-6 py-4"></th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="px-0 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Submitted At</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-20 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto" />
                    <p className="text-sm text-muted-foreground mt-3 font-medium">Fetching submissions...</p>
                  </td>
                </tr>
              ) : filteredSubmissions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
                      <div className="w-16 h-16 rounded-3xl bg-muted/30 flex items-center justify-center">
                        <Database className="w-8 h-8 text-muted-foreground/50" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-foreground">No submissions found</p>
                        <p className="text-xs text-muted-foreground">Submissions matching your filters will appear here.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSubmissions.map((submission) => (
                  <tr 
                    key={submission.id} 
                    className={`group transition-all hover:bg-muted/30 ${submission.is_read === 0 ? 'bg-primary/5 hover:bg-primary/10' : ''}`}
                  >
                     <td className="px-6 py-4">
                       <button
                         onClick={() => handleToggleRead(submission.id, submission.is_read)}
                         disabled={actionLoading === submission.id}
                         title={submission.is_read === 1 ? "Mark as unread" : "Mark as read"}
                         className={`transition-colors ${submission.is_read === 1 ? 'text-muted-foreground/30 hover:text-primary' : 'text-primary'}`}
                       >
                         {actionLoading === submission.id ? (
                           <Loader2 className="w-4 h-4 animate-spin" />
                         ) : submission.is_read === 1 ? (
                           <Eye className="w-4 h-4" />
                         ) : (
                           <motion.div initial={{scale: 0.8}} animate={{scale: 1}}><EyeOff className="w-4 h-4" /></motion.div>
                         )}
                       </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm tracking-tight ${submission.is_read === 0 ? 'font-bold text-foreground' : 'font-medium text-foreground/80'}`}>
                        {submission.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm ${submission.is_read === 0 ? 'font-semibold text-foreground/70' : 'text-muted-foreground'}`}>
                        {submission.email}
                      </span>
                    </td>
                    <td className="px-0 py-4">
                      <span className="text-xs font-medium text-muted-foreground">
                        {new Date(submission.submitted_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => setSelectedSubmission(submission)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-xs font-bold text-foreground hover:bg-muted transition-all shadow-sm"
                        >
                          View <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {selectedSubmission && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedSubmission(null)}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] mt-0"
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-2xl z-[10000] p-6 overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Submission Details</h3>
                    <p className="text-xs text-muted-foreground">{selectedSubmission.form_name} - {selectedSubmission.plugin}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedSubmission(null)}
                    className="p-2 rounded-lg bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-6">
                  {(Array.isArray(selectedSubmission.data) ? selectedSubmission.data : [])
                    .filter(field => {
                      const label = (field.label || '').toUpperCase();
                      // Hide technical meta-data and redundant fields
                      const isTechnical = 
                        label.includes('NONCE') || 
                        label.includes('REFERER') || 
                        label.includes('TOKEN') ||
                        label.includes('NAMES') || 
                        label.includes('EMAIL');
                      return !isTechnical;
                    })
                    .map((field, idx) => (
                      <div key={idx} className="space-y-1.5 group">
                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{field.label}</label>
                        <div className="bg-muted/30 border border-border/50 rounded-xl px-4 py-3">
                          {field.type === 'attachment' ? (
                            <a 
                              href={field.value} 
                              target="_blank" 
                              rel="noreferrer"
                              className="flex items-center gap-3 text-primary hover:underline text-sm font-semibold"
                            >
                              <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 shrink-0">
                                <Download className="w-4 h-4" />
                              </div>
                              <span className="truncate">{field.label} Attachment</span>
                            </a>
                          ) : (
                            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{field.value || '(Empty)'}</p>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
